import { IconButton } from "@mui/material";
import React from "react";
import styled from "styled-components/macro";
import { useTtsQuery, ttsQueryOptions } from "queries/ttsQuery";
import { useLongPress, useUnmount, useMount } from "react-use";
import { useSeeHint } from "../hint/Hint";
import { hashCode, silenceTts } from "queries/tts/ttsTools";
import { LONGPRESSHINT, LONGPRESSHINT2 } from "../picker/SpeechHintTooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVolume, faSpinner } from "@duocards/pro-regular-svg-icons";
import { snackbar } from "tools/events";
import { Trans } from "react-i18next";
import { useGetState } from "components/ReduxProvider";
import { useStateWithRef } from "hooks/useStateWithRef";

enum AudioState {
  idle,
  loading,
  playing
}

const Wrap = styled.span`
  width: 16px;
  height: 1em;
  display: inline-block;
  user-select: none;
  /* margin-top: -12px; */
`;
const SIconButton = styled(IconButton)<{ $small?: boolean }>`
  padding: ${({ $small }) => ($small ? "12px" : "15px")};
  padding-right: 2px; /* to not ovelap with selectable text */
  margin: ${({ $small }) => ($small ? "-4px 0 0 -23px;" : "-4px 0 0 -34px;")};
  color: ${({ theme }) => theme.duo.color.lightBlue};
  transition:
    color 1s,
    transform 0.8s;
  svg {
    width: ${({ $small }) => ($small ? "20" : "26")}px;
    height: ${({ $small }) => ($small ? "20" : "26")}px;
  }
`;

const VolumeWithExclamation = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.duo.color.textGrey};
  font-weight: 600;
  font-size: 16px;
  svg {
    color: ${({ theme }) => theme.duo.color.textGrey};
  }
  &::before {
    content: "!";
    margin-left: 4px;
    color: ${({ theme }) => theme.duo.color.textGrey};
    font-weight: 600;
    font-size: 16px;
    position: relative;
  }
`;

type Props = {
  text?: string;
  lang?: string;
  small?: boolean;
  className?: string;
  voiceId?: string;
  hash?: string;
  useServerCache?: boolean;
  onClick?: VoidFunction;
  style?: React.CSSProperties;
  playOnMount?: boolean;
  zeroVolumeGuard?: boolean;
  onEnded?: VoidFunction;
  ttsQueryOptions?: ttsQueryOptions;
};

const Speech: React.FC<Props> = React.memo(
  ({ voiceId, onClick, lang, style, playOnMount, useServerCache = true, zeroVolumeGuard, onEnded, ...p }) => {
    const seeHint = useSeeHint();
    const [audioState, setAudioState, audioStateRef] = useStateWithRef(AudioState.idle);
    const [pressing, setPressing] = React.useState(false);
    const unMounted = React.useRef(false);
    const volume = useGetState("volume");
    const { multiLangs } = p.ttsQueryOptions || {};
    const tts = useTtsQuery(lang, p.ttsQueryOptions);

    useMount(() => {
      if (playOnMount) playTTSSafe();
    });

    useUnmount(() => {
      unMounted.current = true;
      if (audioStateRef.current === AudioState.playing) silenceTts();
    });

    const cursorCoords = React.useRef({
      startX: 0,
      startY: 0,
      endX: 0,
      endY: 0
    });

    const getX = (e) => e.pageX || (e.touches?.[0] || e.changedTouches?.[0])?.pageX;
    const getY = (e) => e.pageY || (e.touches?.[0] || e.changedTouches?.[0])?.pageY;

    const playTTSSafe = (speakingRate?: number, raiseSnackbar?: boolean) => {
      if (zeroVolumeGuard && volume === 0) {
        raiseSnackbar &&
          snackbar(<Trans>Your device is muted.</Trans>, {
            severity: "warning"
          });
        return;
      }
      playTTS(speakingRate);
    };

    const playTTS = (speakingRate?: number) => {
      if (!tts || !p.text) return;
      setAudioState(AudioState.loading);
      tts({
        text: p.text as string,
        speakingRate: speakingRate,
        voiceId,
        onStarted: () => {
          if (unMounted.current) silenceTts();
          setAudioState(AudioState.playing);
        },
        onEnded: () => {
          if (audioStateRef.current === AudioState.playing || audioStateRef.current === AudioState.loading) {
            onEnded?.();
          }
          setAudioState(AudioState.idle);
        },
        useServerCache
      });
    };

    const longPress = useLongPress(
      () => {
        const deltaX = Math.abs(cursorCoords.current.startX - cursorCoords.current.endX);
        const deltaY = Math.abs(cursorCoords.current.startY - cursorCoords.current.endY);
        const isDragging = deltaX > 30 || deltaY > 30;

        if (tts && !isDragging) {
          playTTSSafe(0.6, true); // SLOW RATE
          seeHint(LONGPRESSHINT);
          seeHint(LONGPRESSHINT2);
        }
        setPressing(true);
      },
      {
        delay: 200,
        isPreventDefault: false
      }
    );

    if (!tts && (!multiLangs || multiLangs.length === 0)) return null;
    if (!p.text) return null;

    const handlePlayClick = (e) => {
      if (!pressing) {
        playTTSSafe(undefined, true); // NORMAL RATE
      }
      e.stopPropagation();
    };

    const handleClick = (e) => {
      onClick && onClick();
      if (audioStateRef.current === AudioState.playing) {
        silenceTts();
      } else {
        handlePlayClick(e);
      }
    };

    const handleDown = (e) => {
      cursorCoords.current.startX = cursorCoords.current.endX = getX(e);
      cursorCoords.current.startY = cursorCoords.current.endY = getY(e);
      longPress.onMouseDown(e);
      setTimeout(() => (window as any).getSelection && (window as any).getSelection().removeAllRanges(), 500); // ios quirks
      setTimeout(() => (window as any).getSelection && (window as any).getSelection().removeAllRanges(), 550);
    };
    const handleUp = (e) => {
      setTimeout(() => setPressing(false), 50);
      (window as any).getSelection && (window as any).getSelection().removeAllRanges();
      longPress.onMouseUp();
    };

    const handleMove = (e) => {
      cursorCoords.current.endX = getX(e);
      cursorCoords.current.endY = getY(e);
    };

    return (
      <Wrap className={p.className}>
        {!tts && (
          <SIconButton
            $small={p.small}
            color="primary"
            onClick={() =>
              snackbar(<Trans>Text-to-speech unavailable for this language combination.</Trans>, {
                severity: "info"
              })
            }
          >
            <VolumeWithExclamation>
              <FontAwesomeIcon icon={faVolume} />
            </VolumeWithExclamation>
          </SIconButton>
        )}

        {tts && (
          <SIconButton
            onMouseDown={handleDown}
            onMouseMove={handleMove}
            onTouchMove={handleMove}
            onTouchStart={handleDown}
            onClick={handleClick}
            onMouseUp={handleUp}
            onTouchEnd={handleUp}
            onMouseLeave={handleUp}
            $small={p.small}
            color="primary"
            className={hashCode(p.text, voiceId)}
          >
            {audioState === AudioState.loading ? (
              <FontAwesomeIcon icon={faSpinner} spin={true} />
            ) : (
              <FontAwesomeIcon icon={faVolume} beatFade={audioState === AudioState.playing} />
            )}
          </SIconButton>
        )}
      </Wrap>
    );
  }
);

// WORKAROUND because in voiceItem I cannot declare this for unnown reason
export const SpeechForVoice = styled(Speech)`
  width: auto;
  margin: -11px 0 0 -3px;
  .MuiIconButton-root {
    padding: 0 5px;
    margin: 0;
  }
  svg {
    width: 26px !important;
    height: 26px !important;
  }
`;

export default Speech;
